import React, { useState, useEffect, useMemo } from "react";

import { useParams, useNavigate } from "react-router-dom";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  ArrowLeft,
  Wallet,
  Landmark,
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  FileText,
  Upload,
  Trash2,
  Eye,
  Plus,
  Search,
} from "lucide-react";

import { toast } from "react-toastify";

import "../styles/investorAccount.css";

import logo from "../assets/logo.png";

const API_BASE_URL =
  "https://client-management-backend-8x91.onrender.com";

const InvestorAccount = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [investor, setInvestor] = useState(null);

  const [transactions, setTransactions] = useState([]);

  const [documents, setDocuments] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] =
    useState("transactions");

  const [docDescription, setDocDescription] =
    useState("");

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!investor) return;

    try {
      const docs =
        Array.isArray(investor.documents)
          ? investor.documents
          : typeof investor.documents === "string"
          ? JSON.parse(investor.documents)
          : [];

      setDocuments(docs);
    } catch (e) {
      console.error("Failed to parse docs", e);
      setDocuments([]);
    }
  }, [investor]);

  useEffect(() => {
    if (!id || id === "undefined") {
      setError("Invalid Investor ID");
      return;
    }

    const fetchData = async () => {
      try {
        const investorRes = await fetch(
          `${API_BASE_URL}/investors/${id}`
        );

        if (!investorRes.ok)
          throw new Error("Investor not found");

        const investorData =
          await investorRes.json();

        setInvestor(investorData);

        const txnRes = await fetch(
          `${API_BASE_URL}/transactions/${id}`
        );

        if (!txnRes.ok)
          throw new Error(
            "Failed to fetch transactions"
          );

        const txnData = await txnRes.json();

        setTransactions(
          Array.isArray(txnData.transactions)
            ? txnData.transactions
            : []
        );
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort(
      (a, b) =>
        new Date(a.transaction_date) -
        new Date(b.transaction_date)
    );
  }, [transactions]);

  const ledgerTransactions = useMemo(() => {
    let runningBalance = 0;

    return sortedTransactions.map((txn) => {
      const amount = parseFloat(txn.amount || 0);

      if (
        txn.transaction_type === "Deposit" ||
        txn.transaction_type === "Interest"
      ) {
        runningBalance += amount;
      }

      if (
        txn.transaction_type === "Withdrawal"
      ) {
        runningBalance -= amount;
      }

      return {
        ...txn,
        runningBalance,
      };
    });
  }, [sortedTransactions]);

  const filteredTransactions =
    ledgerTransactions.filter((txn) => {
      const search =
        searchTerm.toLowerCase();

      return (
        txn.transaction_type
          .toLowerCase()
          .includes(search) ||
        txn.amount
          ?.toString()
          .includes(search)
      );
    });

  const totalDeposits =
    ledgerTransactions
      .filter(
        (t) =>
          t.transaction_type === "Deposit"
      )
      .reduce(
        (sum, t) =>
          sum + parseFloat(t.amount || 0),
        0
      );

  const totalInterest =
    ledgerTransactions
      .filter(
        (t) =>
          t.transaction_type === "Interest"
      )
      .reduce(
        (sum, t) =>
          sum + parseFloat(t.amount || 0),
        0
      );

  const totalWithdrawals =
    ledgerTransactions
      .filter(
        (t) =>
          t.transaction_type ===
          "Withdrawal"
      )
      .reduce(
        (sum, t) =>
          sum + parseFloat(t.amount || 0),
        0
      );

  const netBalance =
    totalDeposits +
    totalInterest -
    totalWithdrawals;

  const handleStatusChange = async (
    newStatus
  ) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/investors/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            status: newStatus,
          }),
        }
      );

      if (!response.ok)
        throw new Error(
          "Failed to update status"
        );

      const updatedInvestor =
        await response.json();

      setInvestor(updatedInvestor);

      toast.success(
        "Status updated successfully"
      );
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    }
  };

  const handleDelete = async () => {
    const confirmDelete =
      window.confirm(
        `Delete ${investor?.name}?`
      );

    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/investors/${id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok)
        throw new Error(
          "Failed to delete investor"
        );

      toast.success(
        "Investor deleted"
      );

      navigate("/investors");
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  };

  const handleUploadDocument =
    async (e) => {
      const file = e.target.files[0];

      if (!file) return;

      if (!docDescription.trim()) {
        toast.warn(
          "Enter a description"
        );
        return;
      }

      const formData = new FormData();

      formData.append("file", file);

      formData.append(
        "description",
        docDescription
      );

      try {
        const response = await fetch(
          `${API_BASE_URL}/investors/${id}/add-document`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data =
          await response.json();

        if (!response.ok)
          throw new Error(data.error);

        setDocuments(data.documents);

        setDocDescription("");

        toast.success(
          "Document uploaded"
        );
      } catch (err) {
        console.error(err);
        toast.error(
          "Upload failed"
        );
      }
    };

  const handleDeleteDocument =
    async (url) => {
      const confirmDelete =
        window.confirm(
          "Delete this document?"
        );

      if (!confirmDelete) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/investors/${id}/document`,
          {
            method: "DELETE",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              url,
            }),
          }
        );

        const data =
          await response.json();

        if (!response.ok)
          throw new Error(data.error);

        setDocuments(data.documents);

        toast.success(
          "Document deleted"
        );
      } catch (err) {
        console.error(err);
        toast.error(
          "Delete failed"
        );
      }
    };

  const generateStatement = () => {
    const doc = new jsPDF();

    doc.setFont("helvetica");

    doc.setFontSize(14);

    doc.text(
      "RCS TOPUP AND BILLING ENTITY",
      15,
      20
    );

    doc.addImage(
      logo,
      "PNG",
      160,
      10,
      35,
      20
    );

doc.setFontSize(11);

doc.setTextColor(80);

doc.text(
  `Investor: ${investor?.name || "N/A"}`,
  15,
  32
);

doc.text(
  `Date Joined: ${
    investor?.date_joined
      ? new Date(
          investor.date_joined
        ).toLocaleDateString()
      : "N/A"
  }`,
  15,
  40
);

doc.text(
  `Date of Maturity: ${
    investor?.date_payable
      ? new Date(
          investor.date_payable
        ).toLocaleDateString()
      : "N/A"
  }`,
  15,
  48
);

doc.text(
  `Investment Term: ${
    investor?.investment_term ||
    "N/A"
  }`,
  120,
  40
);

doc.text(
  `ROI: ${
    investor?.roi || 0
  }%`,
  120,
  48
);

    autoTable(doc, {
      startY: 72,

      head: [
        [
          "Date",
          "Type",
          "Deposit",
          "Interest",
          "Withdrawal",
          "Balance",
        ],
      ],

      body: ledgerTransactions.map(
        (txn) => [
          new Date(
            txn.transaction_date
          ).toLocaleDateString(),

          txn.transaction_type,

          txn.transaction_type ===
          "Deposit"
            ? `$${parseFloat(
                txn.amount
              ).toFixed(2)}`
            : "-",

          txn.transaction_type ===
          "Interest"
            ? `$${parseFloat(
                txn.amount
              ).toFixed(2)}`
            : "-",

          txn.transaction_type ===
          "Withdrawal"
            ? `$${parseFloat(
                txn.amount
              ).toFixed(2)}`
            : "-",

          `$${txn.runningBalance.toFixed(
            2
          )}`,
        ]
      ),

      theme: "grid",

      headStyles: {
        fillColor: [7, 17, 51],
      },
    });

    doc.save(
      `Investor_Statement_${investor?.name}.pdf`
    );
  };

  if (loading) {
    return (
      <div className="investor-account-loading-screen">
        <div className="investor-account-loader-ring">
          <img
            src={logo}
            alt="logo"
          />
        </div>

        <h1>
          Loading Account
        </h1>

        <p>
          Preparing balances,
          transactions and
          investment records...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="investor-account-error">
        {error}
      </div>
    );
  }

  return (
    <div className="investor-account-modern-page">
      <div className="investor-account-hero-grid">
        <div className="investor-account-hero-left">
          <div className="investor-account-pill">
            Investor Ledger
          </div>

          <h1>
            {investor?.name}
          </h1>

          <p>
            Monitor balances,
            returns, ROI,
            transaction history
            and investor
            documents from one
            modern financial
            dashboard.
          </p>

          <div className="investor-account-hero-actions">
            <button
              className="investor-account-primary-btn"
              onClick={() =>
                navigate("/investors")
              }
            >
              <ArrowLeft size={18} />
              Back
            </button>

            <button
              className="investor-account-secondary-btn"
              onClick={
                generateStatement
              }
            >
              <FileText size={18} />
              Statement
            </button>
          </div>
        </div>

        <div className="investor-account-balance-card">
          <div className="investor-account-balance-top">
            <span>
              Current Balance
            </span>

            <Wallet size={20} />
          </div>

          <h2>
            $
            {parseFloat(
              investor?.current_balance ||
                0
            ).toLocaleString()}
          </h2>

          <div className="investor-account-balance-growth">
            +ROI Active
          </div>
        </div>
      </div>

      <div className="investor-account-stats-grid">
        <div className="investor-account-stat-card">
          <div className="investor-account-stat-icon teal">
            <TrendingUp size={22} />
          </div>

          <div>
            <span>
              Deposits
            </span>

            <h3>
              $
              {totalDeposits.toLocaleString()}
            </h3>
          </div>
        </div>

        <div className="investor-account-stat-card">
          <div className="investor-account-stat-icon green">
            <Landmark size={22} />
          </div>

          <div>
            <span>
              Interest Earned
            </span>

            <h3>
              $
              {totalInterest.toLocaleString()}
            </h3>
          </div>
        </div>

        <div className="investor-account-stat-card">
          <div className="investor-account-stat-icon red">
            <TrendingDown size={22} />
          </div>

          <div>
            <span>
              Withdrawals
            </span>

            <h3>
              $
              {totalWithdrawals.toLocaleString()}
            </h3>
          </div>
        </div>

        <div className="investor-account-stat-card">
          <div className="investor-account-stat-icon blue">
            <ShieldCheck size={22} />
          </div>

          <div>
            <span>
              Net Balance
            </span>

            <h3>
              $
              {netBalance.toLocaleString()}
            </h3>
          </div>
        </div>
      </div>

      <div className="investor-account-tabs">
        <button
          className={
            activeTab ===
            "transactions"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveTab(
              "transactions"
            )
          }
        >
          Transactions
        </button>

        <button
          className={
            activeTab ===
            "documents"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveTab(
              "documents"
            )
          }
        >
          Documents
        </button>

        <button
          className={
            activeTab ===
            "profile"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveTab(
              "profile"
            )
          }
        >
          Profile
        </button>
      </div>

      {activeTab ===
        "transactions" && (
        <>
          <div className="investor-account-table-toolbar">
            <div className="investor-account-search">
              <Search size={18} />

              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          <div className="investor-account-table-wrapper">
            <table className="investor-account-modern-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>
                    Transaction
                  </th>
                  <th>
                    Deposit
                  </th>
                  <th>
                    Interest
                  </th>
                  <th>
                    Withdrawal
                  </th>
                  <th>
                    Balance
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredTransactions.length >
                0 ? (
                  filteredTransactions.map(
                    (
                      txn,
                      index
                    ) => (
                      <tr
                        key={
                          index
                        }
                      >
                        <td>
                          {new Date(
                            txn.transaction_date
                          ).toLocaleDateString()}
                        </td>

                        <td>
                          <span
                            className={`investor-account-txn-badge ${txn.transaction_type.toLowerCase()}`}
                          >
                            {
                              txn.transaction_type
                            }
                          </span>
                        </td>

                        <td>
                          {txn.transaction_type ===
                          "Deposit"
                            ? `$${parseFloat(
                                txn.amount
                              ).toFixed(
                                2
                              )}`
                            : "-"}
                        </td>

                        <td>
                          {txn.transaction_type ===
                          "Interest"
                            ? `$${parseFloat(
                                txn.amount
                              ).toFixed(
                                2
                              )}`
                            : "-"}
                        </td>

                        <td>
                          {txn.transaction_type ===
                          "Withdrawal"
                            ? `$${parseFloat(
                                txn.amount
                              ).toFixed(
                                2
                              )}`
                            : "-"}
                        </td>

                        <td className="investor-account-balance-column">
                          $
                          {txn.runningBalance.toFixed(
                            2
                          )}
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="investor-account-empty"
                    >
                      No
                      transactions
                      found.
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td
                    colSpan="2"
                  >
                    Totals
                  </td>

                  <td>
                    $
                    {totalDeposits.toFixed(
                      2
                    )}
                  </td>

                  <td>
                    $
                    {totalInterest.toFixed(
                      2
                    )}
                  </td>

                  <td>
                    $
                    {totalWithdrawals.toFixed(
                      2
                    )}
                  </td>

                  <td className="investor-account-final-balance">
                    $
                    {netBalance.toFixed(
                      2
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {activeTab ===
        "documents" && (
        <div className="investor-account-documents-panel">
          <div className="investor-account-upload-row">
            <input
              type="text"
              placeholder="Document description"
              value={
                docDescription
              }
              onChange={(e) =>
                setDocDescription(
                  e.target.value
                )
              }
            />

            <label className="investor-account-upload-btn">
              <Upload size={18} />
              Upload

              <input
                hidden
                type="file"
                accept="application/pdf,image/*"
                onChange={
                  handleUploadDocument
                }
              />
            </label>
          </div>

          <div className="investor-account-documents-grid">
            {documents.length >
            0 ? (
              documents.map(
                (doc, i) => (
                  <div
                    key={i}
                    className="investor-account-document-card"
                  >
                    <div className="investor-account-document-top">
                      <FileText size={22} />

                      <span>
                        {new Date(
                          doc.created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    <h4>
                      {
                        doc.description
                      }
                    </h4>

                    <div className="investor-account-document-actions">
                      <a
                        href={
                          doc.url
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Eye
                          size={
                             18
                          }
                        />
                      </a>

                      <button
                        onClick={() =>
                          handleDeleteDocument(
                            doc.url
                          )
                        }
                      >
                        <Trash2
                          size={
                            18
                          }
                        />
                      </button>
                    </div>
                  </div>
                )
              )
            ) : (
              <div className="investor-account-empty-docs">
                No documents
                uploaded.
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab ===
        "profile" && (
        <div className="investor-account-profile-grid">
          <div className="investor-account-profile-card">
            <span>
              Account Type
            </span>

            <h4>
              {
                investor?.account_type
              }
            </h4>
          </div>

          <div className="investor-account-profile-card">
            <span>
              ROI
            </span>

            <h4>
              {parseFloat(
                investor?.roi ||
                  0
              ).toFixed(2)}
              %
            </h4>
          </div>

          <div className="investor-account-profile-card">
            <span>
              Investment
              Term
            </span>

            <h4>
              {
                investor?.investment_term
              }
            </h4>
          </div>
<div className="investor-account-profile-card">
  <span>
    Date Joined
  </span>

  <h4>
    {investor?.date_joined
      ? new Date(
          investor.date_joined
        ).toLocaleDateString()
      : "N/A"}
  </h4>
</div>

<div className="investor-account-profile-card">
  <span>
    Date of Maturity
  </span>

  <h4>
    {investor?.date_payable
      ? new Date(
          investor.date_payable
        ).toLocaleDateString()
      : "N/A"}
  </h4>
</div>
          <div className="investor-account-profile-card">
            <span>
              Status
            </span>

            <select
              value={
                investor?.status ||
                "Active"
              }
              onChange={(e) =>
                handleStatusChange(
                  e.target.value
                )
              }
            >
              <option value="Active">
                Active
              </option>

              <option value="Inactive">
                Inactive
              </option>
            </select>
          </div>
        </div>
      )}

      <div className="investor-account-footer-actions">
        <button
          className="investor-account-delete-btn"
          onClick={
            handleDelete
          }
        >
          <Trash2 size={18} />
          Delete Investor
        </button>
      </div>
    </div>
  );
};

export default InvestorAccount;